/* eslint-disable arrow-body-style */
import { sortGenreSourcesGenreOptions } from '@intelligence/components/GenreSelect/utils';
import {
  getRelationChangeSourceGenreCheckboxOptionChildrenMapper,
  getShortcutsSourceGenreCheckboxOptionChildrenMapperBuilder,
} from '@intelligence/components/GenreSelect/reducers/mappers';
import { Relation, ShortcutActionType, ShortcutMatchType } from '@intelligence/components/GenreSelect/enums';
import {
  MAX_SUB_GENRES_OF_RELATION_AND,
  MAX_SUB_GENRES_SELECT_MSG,
  MAX_SUB_GENRES_SELECT_MSG_CN,
} from '@intelligence/components/GenreSelect/constants';

import type {
  IOption,
  IGenreValuable,
  IGenreCheckboxOption,
  IGenreCheckboxOptionWithChildren,
  IGenreSourcesGenreCheckboxOptionsWithChildren,
} from '@intelligence/components/GenreSelect/types';
import type {
  IGenreValuableMap,
  IGenreSourcesGenreCheckboxOptionsBuilder,
  IGenreSourcesGenreCheckboxOptionsUpdater,
  ISubGenreCheckedChangeHandler,
  ISubGenreBatchCheckedChangeHandler,
  ISubGenreAllCheckedChangeHandler,
  IGenreCheckedChangeHandler,
  IGenreBatchCheckedChangeHandler,
  IGenreAllCheckedChangeHandler,
  IGenreRelationChangeHandler,
  IGenreShortcutsHandler,
  IGenreDeleteHandler,
  IGenreDeleteItemHandler,
  IGenreDeleteBatchHandler,
} from '@intelligence/components/GenreSelect/reducers/types';

/**
 * 把品类value对象集合转换为品类value与品类value对象的map映射，键为品类value，值为品类value对象（可以是主品类、也可以是副品类）
 * 方便通过品类value快速获取品类value对象，比如主品类的relation、children、checked、indeterminate等信息，减少遍历次数提升效率
 * @param genres 品类value对象集合
 * @returns 品类value与品类value对象的map映射
 */
const getGenresMap = (genres: IGenreValuable[]): IGenreValuableMap => {
  if (!Array.isArray(genres) || !genres.length) return {};
  return genres.reduce<IGenreValuableMap>((genresMap, genre) => {
    const genreValue = genre?.value;
    if (!genreValue) return genresMap;
    return { ...genresMap, [genreValue]: genre };
  }, {});
};

/**
 * 判定指定副品类Checkbox的Option是否禁用
 * 禁用副品类，需要满足以下3个条件
 * 1. 从属的主品类的relation是and
 * 2. 从属的主品类下已勾选的副品类数量大于等于MAX_SUB_GENRES_OF_RELATION_AND配置的数值
 * 3. 指定副品类Checkbox的Option的checked（最新值）为false（即没有被勾选，最新的勾选状态）
 * @param relation 指定副品类从属的主品类的relation
 * @param checkedLength 指定副品类从属的主品类下已勾选的副品类数量
 * @param checked 指定副品类勾选状态（最新的勾选状态）
 * @returns 指定副品类是否禁用
 */
export const getIsSubGenreDisabled = (relation: Relation, checkedLength: number, checked: boolean): boolean => {
  return relation === Relation.And && checkedLength >= MAX_SUB_GENRES_OF_RELATION_AND && !checked;
};

/**
 * 判断指定索引的副品类是否需要禁用（当勾选、取消勾选主品类时）
 * 仅当勾选主品类，并且当前勾选的主品类relation是Relation.And时 才会禁用当前主品类下第MAX_SUB_GENRES_OF_RELATION_AND个以后的副品类
 * 其余情况不禁用副品类
 * @param relation 指定索引的副品类归属的主品类的relation
 * @param checked 指定索引的副品类归属的主品类 是否勾选
 * @param i 指定的索引（只针对其归属的主品类下的索引）
 * @return 指定索引的副品类是否应该被禁用
 */
export const getIsSubGenreDisabledWhenGenreCheckedChange = (
  relation: Relation,
  checked: boolean,
  i: number,
): boolean => {
  if (!checked) return false;
  return relation === Relation.Or ? false : i >= MAX_SUB_GENRES_OF_RELATION_AND;
};

/**
 * 判断指定索引的副品类是否需要被勾选（当勾选、取消勾选主品类时）
 * 取消勾选主品类时，当前主品类下所有副品类也取消勾选
 * 勾选主品类时
 *  1.当前主品类的relation是Relation.And 那么仅勾选当前主品类下的前MAX_SUB_GENRES_OF_RELATION_AND个副品类
 *  2.当前主品类的relation是Relation.Or,并且是非搜索状态下，那么勾选当前主品类下的所有副品类
 *  2.当前主品类的relation是Relation.Or，搜索状态下，那么只勾选当前主品类下搜索过滤后的副品类
 * @param relation 指定索引的副品类归属的主品类的relation
 * @param checked 指定索引的副品类归属的主品类 是否勾选
 * @param i 指定的索引（只针对其归属的主品类下的索引）
 * @return 指定副品类是否应该被勾选
 */
export const getIsSubGenreCheckedWhenGenreCheckedChange = (
  relation: Relation,
  checked: boolean,
  i: number,
  subGenreCheckboxOption: IGenreCheckboxOption,
  genresFilteredObjChildren?: IGenreCheckboxOption[],
): boolean => {
  // 取消勾选
  if (!checked) return false;
  // Or关系下
  if (relation === Relation.Or) {
    // 判断是否为搜索状态下
    if (Array.isArray(genresFilteredObjChildren) && genresFilteredObjChildren?.length > 0) {
      // 若匹配上搜索过滤后的副品类，则勾选
      if (genresFilteredObjChildren?.findIndex((item) => item?.value === subGenreCheckboxOption?.value) > -1) {
        return true;
      }
      // 否则，返回未匹配上的副品类，原有勾选状态
      return subGenreCheckboxOption?.checked;
    }
    // 非搜索下，全选
    return true;
  }
  // And
  return i < MAX_SUB_GENRES_OF_RELATION_AND;
};

/**
 * 根据主品类的relation、勾选状态、主品类下的副品类Checkbox Options集合 获取 最新的副品类Checkbox Options集合
 * 用于勾选、取消勾选主品类时 获取当前主品类下最新的副品类Checkbox Options集合
 * 用于全选、取消全选主品类时，获取指定主品类下最新的副品类Checkbox Options集合
 * @param relation 主品类relation
 * @param checked 主品类是否勾选
 * @param subGenreCheckboxOptions 主品类下的副品类Checkbox Options集合
 * @returns 主品类下的副品类Checkbox Options集合（已更新为最新状态）
 */
const getSubGenreCheckboxOptionsWhenGenreCheckedChange = (
  relation: Relation,
  checked: boolean,
  subGenreCheckboxOptions?: IGenreCheckboxOption[],
  genresFilteredObjChildren?: IGenreCheckboxOption[],
): IGenreCheckboxOption[] => {
  if (!Array.isArray(subGenreCheckboxOptions) || !subGenreCheckboxOptions.length) return [];
  return subGenreCheckboxOptions.map((subGenreCheckboxOption, subGenreCheckboxOptionIdx) => {
    const subGenreCheckboxOptionDisabled = getIsSubGenreDisabledWhenGenreCheckedChange(
      relation,
      checked,
      subGenreCheckboxOptionIdx,
    );
    const subGenreCheckboxOptionChecked = getIsSubGenreCheckedWhenGenreCheckedChange(
      relation,
      checked,
      subGenreCheckboxOptionIdx,
      subGenreCheckboxOption,
      genresFilteredObjChildren,
    );
    return {
      ...subGenreCheckboxOption,
      disabled: subGenreCheckboxOptionDisabled,
      checked: subGenreCheckboxOptionChecked,
    };
  });
};

/**
 * 计算当前副品类Checkbox Options集合 是否全部勾选、是否非禁用的全部勾选、是否有勾选的
 * 这里没有Array.prototype.some、Array.prototype.every这两个方法，减少一次for循环
 * @param subGenreCheckboxOptions 副品类Checkbox Options集合
 * @returns 返回当前副品类Checkbox Options集合 是否全部勾选、是否有勾选的
 */
const getSubGenreCheckboxOptionsCheckedState = (
  subGenreCheckboxOptions?: IGenreCheckboxOption[],
): {
  everyChecked: boolean;
  someChecked: boolean;
} => {
  if (!Array.isArray(subGenreCheckboxOptions)) return { everyChecked: false, someChecked: false };
  // 是否全部勾选
  let everyChecked = true;
  // 是否有勾选的
  let someChecked = false;
  for (const subGenreCheckboxOption of subGenreCheckboxOptions) {
    if (subGenreCheckboxOption?.checked) {
      // 有勾选的副品类Checkbox Option
      someChecked = true;
    } else {
      // 不是每一个副品类Checkbox Option都是勾选的
      everyChecked = false;
    }
    // everyChecked、someChecked 两个个状态都修改过，就不用后续的循环了，直接结束
    if (!everyChecked && someChecked) break;
  }
  return {
    everyChecked,
    someChecked,
  };
};

/**
 * 根据副品类集合的勾选状态 计算 副品类集合归属的主品类的checked、indeterminate状态
 * @param params 参数对象
 * @param params.everyChecked 是否每一个副品类都勾选了
 * @param params.someChecked 是否有一个副品类勾选了
 * @returns 副品类集合归属的主品类的checked、indeterminate状态
 */
const getGenreCheckboxOptionCheckedState = (params: {
  everyChecked: boolean;
  someChecked: boolean;
}): {
  checked: boolean;
  indeterminate: boolean;
} => {
  const { everyChecked, someChecked } = params || {};
  return {
    checked: someChecked,
    indeterminate: !everyChecked && someChecked,
  };
};

/**
 * 获取有效的勾选的副品类value集合，根据relation业务要求调整
 * @param params.relation 主品类的relation
 * @param params.subGenreCheckboxOptions 主品类下的副品类Checkbox的Options集合
 * @param params.subGenre 本次操作传入的副品类value集合
 * @param params.checked 本次操作是勾选（批量勾选）或者 取消勾选（批量取消勾选）
 * @return 本次操作 根据 relation 限制，调整后的最新的副品类value集合
 */
const getValidCheckedSubGenreValues = (params: {
  relation: Relation;
  subGenreCheckboxOptions: IGenreCheckboxOption[];
  subGenres: string[];
  checked: boolean;
}): string[] => {
  const { relation, subGenreCheckboxOptions, subGenres, checked } = params || {};
  if (!relation) return [];
  if (!Array.isArray(subGenreCheckboxOptions) || !subGenreCheckboxOptions.length) return [];
  if (!Array.isArray(subGenres) || !subGenres.length) return [];
  // 调整后的副品类value集合
  let subGenreValuesNext: string[] = [];

  // 当前主品类下已勾选的副品类value集合（当前的，还未计算本次勾选操作后的状态）
  const subGenreValuesCurrent = subGenreCheckboxOptions
    .filter((subGenreCheckboxOption) => subGenreCheckboxOption?.value && subGenreCheckboxOption?.checked)
    .map((subGenreCheckboxOption) => subGenreCheckboxOption.value);

  // 当前主品类下已勾选的副品类value集合（过滤掉subGenres中包含的副品类value）（当前的，还未计算本次勾选操作后的状态）
  const subGenreValueCurrentFiltered = subGenreValuesCurrent.filter(
    (subGenreValue) => !subGenres.includes(subGenreValue),
  );

  if (relation === Relation.Or) {
    subGenreValuesNext = checked ? [...subGenreValuesCurrent, ...subGenres] : subGenreValueCurrentFiltered;
  }

  if (relation === Relation.And) {
    // 当前主品类下勾选的副品类value集合（调整后的，已计算本次勾选操作后的状态）
    subGenreValuesNext = checked
      ? // 本次是勾选操作或者批量勾选操作，副品类value集合为当前操作传入的副品类value结合 加上 当前已勾选的副品类value集合，当前操作传入的副品类value集合优先级更高
      [...subGenres, ...subGenreValuesCurrent]
      : // 本次是取消勾选、批量取消勾选操作 副品类value集合为当前已勾选副品类value集合过滤掉本次操作传入的副品类value集合中的副品类value
      subGenreValueCurrentFiltered;
    // 副品类value集合的前MAX_SUB_GENRES_OF_RELATION_AND个value（因为relation为and时，会限制允许勾选的个数不超过MAX_SUB_GENRES_OF_RELATION_AND配置的数值）
    subGenreValuesNext = subGenreValuesNext.slice(0, MAX_SUB_GENRES_OF_RELATION_AND);
  }

  return Array.from(new Set(subGenreValuesNext));
};

/**
 * 递归构建子级品类Checkbox的Options集合（支持任意层级）
 * @param options 子级品类Options集合
 * @param valueChildren 当前层级对应的value children集合
 * @param parentValue 父级品类value
 * @param relation 父级品类relation
 * @param checkedLength 父级品类下已勾选的子级数量
 * @returns 子级品类Checkbox的Options集合
 */
const buildSubGenreCheckboxOptionsRecursive = (
  options: IOption[],
  valueChildren: any[],
  parentValue: string,
  relation: Relation,
  checkedLength: number,
): any[] => {
  if (!Array.isArray(options) || options.length === 0) {
    return [];
  }

  // 构建 valueChildren 的 map，便于快速查找
  const valueChildrenMap = getGenresMap(valueChildren as IGenreValuable[]);

  return options.map((option) => {
    const optionValue = option?.value;
    // 当前层级对应的 value 对象
    const valueChild = valueChildrenMap?.[optionValue];
    // @ts-ignore
    const optionChildren = option?.children;

    // 初始化 checked 和 indeterminate
    const checked = !!valueChild;
    const indeterminate = false;

    const result: any = {
      ...option,
      disabled: getIsSubGenreDisabled(relation, checkedLength, checked),
      disabledTips: MAX_SUB_GENRES_SELECT_MSG,
      disabledTipsCN: MAX_SUB_GENRES_SELECT_MSG_CN,
      checked,
      indeterminate,
      parentValue,
    };

    // 递归处理子级（第3、4层级）
    if (Array.isArray(optionChildren) && optionChildren.length > 0) {
      const valueGrandChildren = valueChild?.children || [];
      // @ts-ignore
      result.children = buildSubGenreCheckboxOptionsRecursive(
        optionChildren,
        valueGrandChildren,
        optionValue,
        relation,
        checkedLength,
      );

      // 根据 children 的勾选状态计算当前节点的 checked 和 indeterminate
      const { checked: calculatedChecked, indeterminate: calculatedIndeterminate } = calculateIndeterminateRecursive(
        result.children,
      );

      result.checked = calculatedChecked;
      result.indeterminate = calculatedIndeterminate;
      // 重新计算 disabled（基于更新后的 checked 状态）
      result.disabled = getIsSubGenreDisabled(relation, checkedLength, result.checked);
    }

    return result;
  });
};

/**
 * 构建品类数据源类型与主、副品类Checkbox的Options集合的map映射，根据品类数据源类型与主、副品类Options集合的map映射、品类数据源类型集合，GenreSelect组件value 三者结合生成
 * 1. 根据品类数据源类型集合、品类数据源类型与主、副品类Options集合的map映射 构建默认的品类数据源类型与主、副品类Checkbox的Options集合的map映射
 * 2. 再根据GenreSelect组件value，给默认的品类数据源类型与主、副品类Checkbox的Options集合的map映射对应的品类数据源类型的品类Checkbox的Options集合设置对应的checked、indeterminate、disabled状态
 * @param payload.options 品类数据源类型与主、副品类Options集合的map映射（键为品类数据源类型，值为主、副品类Options集合）
 * @param payload.sources 品类数据源类型集合（即GenreSelect组件支持的品类数据源类型集合，默认为iegg、annie两个品类数据源类型）
 * @param payload.value GenreSelect组件value（由组件引用者传入）
 * @returns 品类数据源类型与主、副品类Checkbox的Options集合的map映射（键为品类数据源类型，值为主、副品类Checkbox的Options集合）（品类筛选器组件渲染就是用这个数据）
 */
export const buildGenreCheckboxOptions: IGenreSourcesGenreCheckboxOptionsBuilder = (payload) => {
  const { options, sources, value } = payload || {};
  // sources配置异常，直接返回空数据
  if (!Array.isArray(sources) || !sources.length) return {};
  // 主、副品类Options排序，按照字母升序排
  const optionsSorted = sortGenreSourcesGenreOptions(options);
  // 遍历品类数据源类型集合
  return sources.reduce((genreCheckboxOptions, source) => {
    if (!source) return genreCheckboxOptions;
    // 当前品类数据源类型对应的主、副品类的Options集合
    const sourceOptions = optionsSorted?.[source];
    // 当前品类数据源类型对应的主、副品类的Options集合异常，返回该品类数据源类型对应的主、副品类Checkbox的Options集合为空数组
    if (!Array.isArray(sourceOptions) || !sourceOptions.length) return { ...genreCheckboxOptions, [source]: [] };
    /**
     * 当前品类数据源类型对应的主、副品类value对象集合
     * 当前品类数据源类型与GenreSelect组件value的品类数据源类型是否一致
     * 一致时 当前品类数据源类型对应的主、副品类value对象集合为GenreSelect组件value的genres
     * 不一致时,当前品类数据源类型对应的主、副品类value对象集合为空数组
     */
    const sourceGenres = value?.source !== source ? [] : value?.genres;
    // 当前品类数据源类型对应的主、副品类value对象集合转换为主品类value与主、副品类value对象集合的map映射，键为主品类value、值为主品类value对象，用来后续通过主品类value，快速获取到主品类value对象
    const sourceGenresMap = getGenresMap(sourceGenres);
    /**
     * 当前品类数据源类型对应的主、副品类Checkbox的Options集合
     * 由当前品类数据源类型对应的主、副品类Options集合、当前品类数据源类型对应的主、副品类value对象集合 两个要素 结合生成
     * 主要是给主、副品类Checkbox的Option 增加 disabled、disabledTips、disabledTipCN、checked、indeterminate、parentValue等信息
     */
    const sourceGenreCheckboxOptions = sourceOptions.map<IGenreCheckboxOptionWithChildren>((option) => {
      const optionValue = option?.value;
      const optionChildren = option?.children || [];
      const optionChildrenLength = optionChildren?.length || 0;
      // 当前品类数据源类型下的当前主品类是否全选
      let optionChecked = false;
      // 当前品类数据源类型下的当前主品类是否半选
      let optionIndeterminate = false;
      /**
       * 与当前品类数据源类型下的当前主品类匹配的主品类value对象
       * 保存了主品类的relation、以及其副品类value对象集合
       * 作为后续给当前主品类的Checkbox的Option追加disabled、disabledTips、disabledTipCN、checked、indeterminate、parentValue等信息的数据支撑
       */
      const sourceGenre = sourceGenresMap?.[optionValue];
      const sourceGenreRelation = sourceGenre?.relation || Relation.Or;
      const sourceGenreChildren = sourceGenre?.children || [];
      const sourceGenreChildrenLength = sourceGenreChildren?.length || 0;
      // 当前品类数据源类型下的当前主品类下的副品类是否全部勾选
      const optionEveryChecked = sourceGenreChildrenLength > 0 && optionChildrenLength <= sourceGenreChildrenLength;
      // 当前品类数据源类型下的当前主品类是否勾选，只要其副品类勾选数量大于0，则归属主品类为勾选状态
      optionChecked = sourceGenreChildrenLength > 0;
      // 当前品类数据源类型下的当前主品类是否半选，只要其不是全选，并且其副品类勾选数量大于0，就是半选状态
      optionIndeterminate = !optionEveryChecked && optionChecked;
      /**
       * 当前品类数据源类型下的当前主品类下的副品类Checkbox的Options集合
       * 使用递归方法构建，支持多层级品类结构
       */
      const sourceSubGenreCheckboxOptions = buildSubGenreCheckboxOptionsRecursive(
        optionChildren,
        sourceGenreChildren,
        optionValue,
        sourceGenreRelation,
        sourceGenreChildrenLength,
      );
      return {
        ...option,
        checked: optionChecked,
        indeterminate: optionIndeterminate,
        relation: sourceGenreRelation,
        children: sourceSubGenreCheckboxOptions || [],
      };
    });
    return {
      ...genreCheckboxOptions,
      [source]: sourceGenreCheckboxOptions,
    };
  }, {});
};

/**
 * 递归更新子级品类Checkbox的Options集合（支持任意层级）
 * @param options 子级品类Checkbox的Options集合
 * @param valueChildren 当前层级对应的value children集合
 * @param relation 父级品类relation
 * @param checkedLength 父级品类下已勾选的子级数量
 * @returns 更新后的子级品类Checkbox的Options集合
 */
const updateSubGenreCheckboxOptionsRecursive = (
  options: any[],
  valueChildren: any[],
  relation: Relation,
  checkedLength: number,
): any[] => {
  if (!Array.isArray(options) || options.length === 0) {
    return [];
  }

  // 构建 valueChildren 的 map，便于快速查找
  const valueChildrenMap = getGenresMap(valueChildren as IGenreValuable[]);

  return options.map((option) => {
    const optionValue = option?.value;
    // 当前层级对应的 value 对象
    const valueChild = valueChildrenMap?.[optionValue];
    // @ts-ignore
    const optionChildren = option?.children;

    // 初始化 checked 和 indeterminate
    const checked = !!valueChild;
    const indeterminate = false;

    const result: any = {
      ...option,
      disabled: getIsSubGenreDisabled(relation, checkedLength, checked),
      checked,
      indeterminate,
    };

    // 递归处理子级
    if (Array.isArray(optionChildren) && optionChildren.length > 0) {
      const valueGrandChildren = valueChild?.children || [];
      // @ts-ignore
      result.children = updateSubGenreCheckboxOptionsRecursive(
        optionChildren,
        valueGrandChildren,
        relation,
        checkedLength,
      );

      // 根据 children 的勾选状态计算当前节点的 checked 和 indeterminate
      const { checked: calculatedChecked, indeterminate: calculatedIndeterminate } = calculateIndeterminateRecursive(
        result.children,
      );

      result.checked = calculatedChecked;
      result.indeterminate = calculatedIndeterminate;
      // 重新计算 disabled（基于更新后的 checked 状态）
      result.disabled = getIsSubGenreDisabled(relation, checkedLength, result.checked);
    }

    return result;
  });
};

/**
 * 根据GenreSelect组件唯一受控value同步所有数据源的Checkbox Options状态，非当前数据源清空勾选缓存
 * @param genreCheckboxOptions 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 * @param value GenreSelect组件value（由组件引用者传入）
 * @returns 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 */
export const updateGenreCheckboxOptionsByValue: IGenreSourcesGenreCheckboxOptionsUpdater = (
  genreCheckboxOptions,
  value,
) => {
  /* ** 前置异常数据校验 ** */
  if (!genreCheckboxOptions) return {};
  if (!value) return genreCheckboxOptions;
  const { source, genres } = value || {};
  // 获取GenreSelect组件value中source对应的的主、副品类Checkbox的Options集合
  const sourceGenreCheckboxOptions = genreCheckboxOptions[source];
  // 数据异常，原样返回，不做状态信息更新
  if (!Array.isArray(sourceGenreCheckboxOptions) || !sourceGenreCheckboxOptions.length) return genreCheckboxOptions;
  /* ** 前置异常数据校验 ** */

  // 生成当前品类数据源类型下的主品类value与主品类value对象的map映射，便于后续通过主品类value访问主品类value对象信息
  const genresMap = getGenresMap(genres);
  // 遍历当前品类数据源类型下的主品类Checkbox的Options集合
  const sourceGenreCheckboxOptionsTmp = sourceGenreCheckboxOptions.map((sourceGenreCheckboxOption) => {
    // 当前品类数据源类型下的当前主品类下的副品类Checkbox的Options集合
    const sourceSubGenreCheckboxOptions = sourceGenreCheckboxOption?.children || [];
    // 当前品类数据源类型下的当前主品类下的副品类Checkbox的Options数量
    const sourceSubGenreCheckboxOptionsLength = sourceSubGenreCheckboxOptions?.length || 0;
    // 获取当前品类数据源类型下的当前主品类value对象信息
    const genre = genresMap?.[sourceGenreCheckboxOption?.value];
    // 获取当前品类数据源类型下的当前主品类的relation
    const sourceGenreCheckboxOptionRelation = genre?.relation || Relation.Or;
    // 当前品类数据源类型下的当前主品类下的副品类value对象集合
    const subGenres = genre?.children;
    // 当前品类数据源类型下的当前主品类下的副品类value对象数量
    const subGenresLength = subGenres?.length || 0;
    // 当前品类数据源类型下的当前主品类是否全部勾选
    const sourceGenreCheckboxOptionEveryChecked =
      subGenresLength > 0 && subGenresLength === sourceSubGenreCheckboxOptionsLength;
    // 当前品类数据源类型下的当前主品类是否勾选
    const sourceGenreCheckboxOptionChecked = subGenresLength > 0;
    // 当前品类数据源类型下的当前主品类是否半选
    const sourceGenreCheckboxOptionIndeterminate =
      !sourceGenreCheckboxOptionEveryChecked && sourceGenreCheckboxOptionChecked;
    // 当前品类数据源类型下的当前主品类下的副品类Checkbox的Options集合（更新状态后的）
    // 使用递归方法更新，支持2-4层级的品类结构
    const sourceGenreCheckboxOptionChildren = updateSubGenreCheckboxOptionsRecursive(
      sourceSubGenreCheckboxOptions,
      subGenres || [],
      sourceGenreCheckboxOptionRelation,
      subGenresLength,
    );
    return {
      ...sourceGenreCheckboxOption,
      relation: sourceGenreCheckboxOptionRelation,
      checked: sourceGenreCheckboxOptionChecked,
      indeterminate: sourceGenreCheckboxOptionIndeterminate,
      children: sourceGenreCheckboxOptionChildren || [],
    };
  });
  return Object.entries(genreCheckboxOptions).reduce<IGenreSourcesGenreCheckboxOptionsWithChildren>(
    (genreCheckboxOptionsTmp, [currentSource, currentSourceGenreCheckboxOptions]) => {
      if (currentSource === source) {
        return {
          ...genreCheckboxOptionsTmp,
          [currentSource]: sourceGenreCheckboxOptionsTmp,
        };
      }

      const clearedSourceGenreCheckboxOptions = currentSourceGenreCheckboxOptions.map((genreCheckboxOption) => {
        return {
          ...genreCheckboxOption,
          relation: Relation.Or,
          checked: false,
          indeterminate: false,
          children: updateSubGenreCheckboxOptionsRecursive(genreCheckboxOption?.children || [], [], Relation.Or, 0),
        };
      });

      return {
        ...genreCheckboxOptionsTmp,
        [currentSource]: clearedSourceGenreCheckboxOptions,
      };
    },
    {},
  );
};

/**
 * 勾选、取消勾选副品类时，更新品类数据源类型与主、副品类Checkbox的Options集合的map映射（主品类的checked、indeterminate、children信息，副品类的disabled、checked信息）
 * @param genreCheckboxOptions 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 * @param payload.source 品类数据源类型
 * @param payload.genre 主品类value
 * @param payload.relation 主品类relation
 * @param payload.subGenre 副品类value
 * @param payload.checked 副品类勾选状态
 * @returns 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 */
export const updateGenreCheckboxOptionsWhenSubGenreCheckedChange: ISubGenreCheckedChangeHandler = (
  genreCheckboxOptions,
  payload,
) => {
  const subGenre = payload?.subGenre;
  if (!subGenre) return genreCheckboxOptions;
  const paylodTmp = {
    source: payload?.source,
    genre: payload?.genre,
    relation: payload?.relation,
    subGenres: [subGenre],
    checked: payload?.checked,
  };
  return updateGenreCheckboxOptionsWhenSubGenreBatchCheckedChange(genreCheckboxOptions, paylodTmp);
};

/**
 * 批量勾选、批量取消勾选副品类时 更新品类数据源类型与主、副品类Checkbox的Options集合的map映射（主品类的checked、indeterminate、children信息，副品类的disabled、checked信息）
 @param genreCheckboxOptions 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 @param payload.source 品类数据源类型
 @param payload.genre 主品类value
 @param payload.relation 主品类relation
 @param payload.subGenres 副品类value集合
 @param payload.checked 副品类勾选状态
 @returns 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 */
export const updateGenreCheckboxOptionsWhenSubGenreBatchCheckedChange: ISubGenreBatchCheckedChangeHandler = (
  genreCheckboxOptions,
  payload,
) => {
  /* ** 前置异常数据校验 ** */
  if (!genreCheckboxOptions) return {};
  const { source, genre, relation, subGenres, checked } = payload || {};
  if (!source || !genre || !relation) return genreCheckboxOptions;
  if (!Array.isArray(subGenres) || !subGenres.length) return genreCheckboxOptions;
  const sourceGenreCheckboxOptions = genreCheckboxOptions[source];
  if (!Array.isArray(sourceGenreCheckboxOptions) || !sourceGenreCheckboxOptions.length) return genreCheckboxOptions;
  /* ** 前置异常数据校验 ** */

  // 指定品类数据源类型的主、副品类Checkbox的Options集合（更新后的）
  const sourceGenreCheckboxOptionsTmp = sourceGenreCheckboxOptions.map((sourceGenreCheckboxOption) => {
    // 当前主品类非指定更新的主品类，不做更新
    if (sourceGenreCheckboxOption?.value !== genre) return { ...sourceGenreCheckboxOption };
    // 当前主品类下的副品类Checkbox的Options集合
    const sourceSubGenreCheckboxOptions = sourceGenreCheckboxOption?.children;
    // 当前主品类下的副品类Checkbox的Options集合数据异常，不做更新
    if (!Array.isArray(sourceSubGenreCheckboxOptions) || !sourceSubGenreCheckboxOptions.length)
      return { ...sourceGenreCheckboxOption };
    // subGenresTmp为根据relation业务规则调整后的最新的副品类value集合
    const subGenresTmp = getValidCheckedSubGenreValues({
      relation,
      subGenreCheckboxOptions: sourceSubGenreCheckboxOptions,
      subGenres,
      checked,
    });
    // 当前主品类下的副品类value集合长度是否超过MAX_SUB_GENRES_OF_RELATION_AND配置的数值（relation and的最大允许勾选数量）
    const isOverMaxSubGenresOfRelationAnd = subGenresTmp.length >= MAX_SUB_GENRES_OF_RELATION_AND;
    // 当前主品类下的副品类Checkbox的Options集合（更新状态后的）
    const sourceSubGenreCheckboxOptionsTmp = sourceSubGenreCheckboxOptions.map((sourceSubGenreCheckboxOption) => {
      // 当前主品类下的当前副品类是否被勾选
      const sourceSubGenreCheckboxOptionChecked = subGenresTmp.includes(sourceSubGenreCheckboxOption?.value);
      if (relation === Relation.Or) {
        return {
          ...sourceSubGenreCheckboxOption,
          checked: sourceSubGenreCheckboxOptionChecked,
        };
      }
      if (relation === Relation.And) {
        return {
          ...sourceSubGenreCheckboxOption,
          /**
           * 当前副品类禁用，需要满足下面3个条件
           * 1、当前主品类的relation为and
           * 2、当前主品类下已勾选的副品类数量大于MAX_SUB_GENRES_OF_RELATION_AND配置的数值
           * 3、当前主品类下的当前副品类没有被勾选
           */
          disabled: isOverMaxSubGenresOfRelationAnd && !sourceSubGenreCheckboxOptionChecked,
          checked: sourceSubGenreCheckboxOptionChecked,
        };
      }
      return { ...sourceSubGenreCheckboxOption };
    });
    // 根据当前主品类下副品类Checkbox的Options集合最新的勾选状态，获取当前主品类最新的勾选、半选状态
    const { checked: sourceGenreCheckboxOptionChecked, indeterminate: sourceGenreCheckboxOptionIndeterminate } =
      getGenreCheckboxOptionCheckedState(getSubGenreCheckboxOptionsCheckedState(sourceSubGenreCheckboxOptionsTmp));
    return {
      ...sourceGenreCheckboxOption,
      checked: sourceGenreCheckboxOptionChecked,
      indeterminate: sourceGenreCheckboxOptionIndeterminate,
      children: sourceSubGenreCheckboxOptionsTmp,
    };
  });

  return {
    ...genreCheckboxOptions,
    [source]: sourceGenreCheckboxOptionsTmp,
  };
};

// 全选、取消全选副品类的操作 相当于 勾选、取消勾选主品类（归属的主品类）操作
export const updateGenreCheckboxOptionsWhenSubGenreAllCheckedChange: ISubGenreAllCheckedChangeHandler = (
  genreCheckboxOptions,
  payload,
) => {
  return updateGenreCheckboxOptionsWhenGenreCheckedChange(genreCheckboxOptions, payload);
};

/**
 * 勾选、取消勾选主品类时，更新品类数据源类型与主、副品类Checkbox的Options集合的map映射，主品类的checked、indeterminate、children信息，副品类的disabled、checked信息
 * @param genreCheckboxOptions 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 * @param payload.source 品类数据源类型
 * @param payload.genre 主品类value
 * @param payload.relation 主品类relation
 * @param payload.checked 主品类勾选状态
 * @returns 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 */
export const updateGenreCheckboxOptionsWhenGenreCheckedChange: IGenreCheckedChangeHandler = (
  genreCheckboxOptions,
  payload,
) => {
  const { source, genre, genresFilteredList = [] } = payload || {};
  if (!source) return genreCheckboxOptions;
  if (!genre) return genreCheckboxOptions;
  const payloadTmp = {
    source,
    genres: [genre],
    checked: payload?.checked,
    genresFilteredList,
  };
  return updateGenreCheckboxOptionsWhenGenreBatchCheckedChange(genreCheckboxOptions, payloadTmp);
};

/**
 * 批量勾选、批量取消勾选主品类时，更新品类数据源类型与主、副品类Checkbox的Options集合的map映射 主品类的checked、indeterminate、children信息，副品类的disabled、checked信息
 * @param genreCheckboxOptions 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 * @param payload.source 品类数据源类型
 * @param payload.genres 主品类集合
 * @param payload.checked 主品类勾选状态
 * @returns 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 */
export const updateGenreCheckboxOptionsWhenGenreBatchCheckedChange: IGenreBatchCheckedChangeHandler = (
  genreCheckboxOptions,
  payload,
) => {
  /* ** 前置异常数据校验 ** */
  if (!genreCheckboxOptions) return {};
  const { source, genres, checked, genresFilteredList = [] } = payload || {};
  if (!source) return genreCheckboxOptions;
  if (!Array.isArray(genres) || !genres.length) return genreCheckboxOptions;
  const sourceGenreCheckboxOptions = genreCheckboxOptions[source];
  if (!Array.isArray(sourceGenreCheckboxOptions) || !sourceGenreCheckboxOptions.length) return genreCheckboxOptions;
  /* ** 前置异常数据校验 ** */

  // 当前品类数据源类型下的主品类Checkbox的Options集合（更新状态后的）
  const sourceGenreCheckboxOptionsTmp = sourceGenreCheckboxOptions.map((sourceGenreCheckboxOption) => {
    // 当前主品类不是本次操作的主品类时，不做更新，直接原样返回
    if (!genres.includes(sourceGenreCheckboxOption?.value)) return { ...sourceGenreCheckboxOption };
    // 当前主品类relation
    const sourceGenreCheckboxOptionRelation = sourceGenreCheckboxOption?.relation || Relation.Or;
    // 当前主品类下勾选的副品类（本次操作更新数据之前的状态）
    const sourceGenreCheckboxOptionChildrenCurrent = sourceGenreCheckboxOption?.children?.filter(
      (item) => !!item?.checked,
    );
    // 当前是勾选操作，并且当前主品类的relation是and，并且当前主品类下勾选的副品类数量刚好达到上限时，不做更新操作，原样返回
    if (
      checked &&
      sourceGenreCheckboxOptionRelation === Relation.And &&
      sourceGenreCheckboxOptionChildrenCurrent?.length === MAX_SUB_GENRES_OF_RELATION_AND
    ) {
      return { ...sourceGenreCheckboxOption };
    }

    // 获取当前主品类对应的搜索过滤的主品类信息
    const genresFilteredObjChildren =
      genresFilteredList?.length > 0
        ? genresFilteredList?.find((item) => item?.value === sourceGenreCheckboxOption?.value)?.children
        : [];
    const sourceGenreCheckboxOptionChildren = getSubGenreCheckboxOptionsWhenGenreCheckedChange(
      sourceGenreCheckboxOptionRelation,
      checked,
      sourceGenreCheckboxOption?.children,
      genresFilteredObjChildren,
    );
    // 根据当前主品类下的副品类Checkbox的Options集合，获取当前主品类的勾选状态、半选状态
    const { checked: sourceGenreCheckboxOptionChecked, indeterminate: sourceGenreCheckboxOptionIndeterminate } =
      getGenreCheckboxOptionCheckedState(getSubGenreCheckboxOptionsCheckedState(sourceGenreCheckboxOptionChildren));
    return {
      ...sourceGenreCheckboxOption,
      checked: sourceGenreCheckboxOptionChecked,
      indeterminate: sourceGenreCheckboxOptionIndeterminate,
      children: sourceGenreCheckboxOptionChildren,
    };
  });
  return {
    ...genreCheckboxOptions,
    [source]: sourceGenreCheckboxOptionsTmp,
  };
};

/**
 * 全选、取消全选主品类时，更新品类数据源类型与主、副品类Checkbox的Options集合的map映射 主品类的checked、indeterminate、children信息，副品类的disabled、checked信息
 * @param genreCheckboxOptions 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 * @param payload.source 品类数据源类型
 * @param payload.checked 主品类勾选状态
 * @returns 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 */
export const updateGenreCheckboxOptionsWhenGenreAllCheckedChange: IGenreAllCheckedChangeHandler = (
  genreCheckboxOptions,
  payload,
) => {
  /* ** 前置异常数据校验 ** */
  if (!genreCheckboxOptions) return {};
  const { source, checked } = payload || {};
  if (!source) return genreCheckboxOptions;
  const sourceGenreCheckboxOptions = genreCheckboxOptions[source];
  if (!Array.isArray(sourceGenreCheckboxOptions) || !sourceGenreCheckboxOptions.length) return genreCheckboxOptions;
  /* ** 前置异常数据校验 ** */

  // 当前品类数据源类型下的主品类Checkbox的Options集合（更新状态后的）
  const sourceGenreCheckboxOptionsTmp = sourceGenreCheckboxOptions.map((sourceGenreCheckboxOption) => {
    // 当前主品类relation
    const sourceGenreCheckboxOptionRelation = sourceGenreCheckboxOption?.relation || Relation.Or;
    // 获取当前主品类下的副品类Checkbox的Options集合（更新状态后的）
    const sourceGenreCheckboxOptionChildren = getSubGenreCheckboxOptionsWhenGenreCheckedChange(
      sourceGenreCheckboxOptionRelation,
      checked,
      sourceGenreCheckboxOption?.children,
    );
    // 根据副品类Checkbox的Options集合 获取当前主品类的勾选状态、半选状态
    const { checked: sourceGenreCheckboxOptionChecked, indeterminate: sourceGenreCheckboxOptionIndeterminate } =
      getGenreCheckboxOptionCheckedState(getSubGenreCheckboxOptionsCheckedState(sourceGenreCheckboxOptionChildren));
    return {
      ...sourceGenreCheckboxOption,
      checked: sourceGenreCheckboxOptionChecked,
      indeterminate: sourceGenreCheckboxOptionIndeterminate,
      children: sourceGenreCheckboxOptionChildren,
    };
  });
  return {
    ...genreCheckboxOptions,
    [source]: sourceGenreCheckboxOptionsTmp,
  };
};

/**
 * 修改主品类relation时 更新品类数据源类型与主、副品类Checkbox的Options集合的map映射 主品类的relation、checked、indeterminate、children信息，副品类的disabled、checked信息
 * @param genreCheckboxOptions 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 * @param payload.source 品类数据源类型
 * @param payload.genre 主品类value
 * @param payload.relation 主品类relation
 * @returns 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 */
export const updateGenreCheckboxOptionsWhenGenreRelationChange: IGenreRelationChangeHandler = (
  genreCheckboxOptions,
  payload,
) => {
  /* ** 前置异常数据校验 ** */
  if (!genreCheckboxOptions) return {};
  const { source, genre, relation = Relation.Or } = payload || {};
  if (!source) return genreCheckboxOptions;
  const sourceGenreCheckboxOptions = genreCheckboxOptions[source];
  if (!Array.isArray(sourceGenreCheckboxOptions) || !sourceGenreCheckboxOptions.length) return genreCheckboxOptions;
  /* ** 前置异常数据校验 ** */

  // 当前品类数据源类型的主品类Checkbox的Options集合（更新状态后的）
  const sourceGenreCheckboxOptionsTmp = sourceGenreCheckboxOptions.map((sourceGenreCheckboxOption) => {
    // 当前主品类不是本次操作的主品类，不做更新 原样返回
    if (sourceGenreCheckboxOption?.value !== genre) return { ...sourceGenreCheckboxOption };
    // 当前主品类下的副品类Checkbox的Options集合（更新状态前）
    const sourceGenreCheckboxOptionChildren = sourceGenreCheckboxOption?.children || [];
    if (!Array.isArray(sourceGenreCheckboxOptionChildren) || !sourceGenreCheckboxOptionChildren.length)
      return { ...sourceGenreCheckboxOption };
    // 当前主品类下的副品类Checkbox的Options集合的map的转换方法
    const sourceGenreCheckboxOptionChildrenMapper = getRelationChangeSourceGenreCheckboxOptionChildrenMapper({
      relation,
      subGenreCheckboxOptions: sourceGenreCheckboxOptionChildren,
    });
    // 当前主品类下的副品类Checkbox的Options集合（更新状态后）
    const sourceGenreCheckboxOptionChildrenTmp = sourceGenreCheckboxOptionChildren.map(
      sourceGenreCheckboxOptionChildrenMapper,
    );
    // 根据副品类Checkbox的Options集合 获取当前主品类的勾选状态、半选状态
    const { checked: sourceGenreCheckboxOptionChecked, indeterminate: sourceGenreCheckboxOptionIndeterminate } =
      getGenreCheckboxOptionCheckedState(getSubGenreCheckboxOptionsCheckedState(sourceGenreCheckboxOptionChildrenTmp));
    return {
      ...sourceGenreCheckboxOption,
      relation,
      checked: sourceGenreCheckboxOptionChecked,
      indeterminate: sourceGenreCheckboxOptionIndeterminate,
      children: sourceGenreCheckboxOptionChildrenTmp,
    };
  });
  return {
    ...genreCheckboxOptions,
    [source]: sourceGenreCheckboxOptionsTmp,
  };
};

/**
 * 快捷勾选品类时 更新品类数据源类型与主、副品类Checkbox的Options集合的map映射 主品类的checked、indeterminate、children信息，副品类的disabled、checked信息
 * @param genreCheckboxOptions 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 * @param payload.source 品类数据源类型
 * @param payload.shortcut 当前点击快捷勾选按钮信息，包含value、type
 * @returns 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 */
export const updateGenreCheckboxOptionsWhenGenreShortcuts: IGenreShortcutsHandler = (genreCheckboxOptions, payload) => {
  /* ** 前置异常数据校验 ** */
  if (!genreCheckboxOptions) return {};
  const { source, shortcut } = payload || {};
  const {
    value: shortcutValue,
    type: shortcutActionType = ShortcutActionType.Compose,
    disabled = false,
  } = shortcut || {};
  if (!source || !shortcutValue) return genreCheckboxOptions;
  const sourceGenreCheckboxOptions = genreCheckboxOptions[source];
  if (!Array.isArray(sourceGenreCheckboxOptions) || !sourceGenreCheckboxOptions.length) return genreCheckboxOptions;
  /* ** 前置异常数据校验 ** */

  // 副品类Checkbox的Options集合的map方法的工厂方法
  const sourceGenreCheckboxOptionChildrenMapperBuilder =
    getShortcutsSourceGenreCheckboxOptionChildrenMapperBuilder(shortcutActionType);
  // 当前品类数据源类型下的主、副品类Checkbox的Options集合（更新状态后）
  const sourceGenreCheckboxOptionsTmp = sourceGenreCheckboxOptions.map((sourceGenreCheckboxOption) => {
    // 当前主品类下的副品类Checkbox的Options集合（更新状态前）
    const sourceGenreCheckboxOptionChildren = sourceGenreCheckboxOption?.children;
    if (!Array.isArray(sourceGenreCheckboxOptionChildren) || !sourceGenreCheckboxOptionChildren.length)
      return { ...sourceGenreCheckboxOption };
    // 当前主品类下的已勾选的副品类Checkbox的Options集合（更新状态前）
    const sourceGenreCheckboxOptionChildrenChecked = sourceGenreCheckboxOptionChildren.filter((item) => item?.checked);
    // 当前主品类下的已勾选的副品类Checkbox的Options集合长度（更新状态前）
    const sourceGenreCheckboxOptionChildrenCheckedLength = sourceGenreCheckboxOptionChildrenChecked.length;
    // 当前主品类下的未勾选的并且跟当前点击的品类快捷勾选按钮匹配的副品类Checkbox的Options集合（更新状态前）
    const sourceGenreCheckboxOptionChildrenMatched = sourceGenreCheckboxOptionChildren.filter(
      (item) => !item?.checked && item?.value === shortcutValue,
    );
    // 当前主品类下的未勾选的并且跟当前点击的品类快捷勾选按钮匹配的副品类Checkbox的Options集合长度（更新状态前）
    const sourceGenreCheckboxOptionChildrenMatchedLength = sourceGenreCheckboxOptionChildrenMatched.length;
    // 当前主品类下的副品类Checkbox的Options集合 的map方法
    const sourceGenreCheckboxOptionChildrenMapper = sourceGenreCheckboxOptionChildrenMapperBuilder({
      shortcutChecked: disabled,
      shortcutValue,
      relation: sourceGenreCheckboxOption?.relation,
      length: sourceGenreCheckboxOptionChildrenCheckedLength + sourceGenreCheckboxOptionChildrenMatchedLength,
    });
    // 当前主品类下的副品类Checkbox的Options集合（更新状态后）
    const sourceGenreCheckboxOptionChildrenTmp = sourceGenreCheckboxOptionChildren.map(
      sourceGenreCheckboxOptionChildrenMapper,
    );
    // 根据副品类的Checkbox的Options集合 获取当前主品类的勾选状态、半选状态
    const { checked: sourceGenreCheckboxOptionChecked, indeterminate: sourceGenreCheckboxOptionIndeterminate } =
      getGenreCheckboxOptionCheckedState(getSubGenreCheckboxOptionsCheckedState(sourceGenreCheckboxOptionChildrenTmp));
    return {
      ...sourceGenreCheckboxOption,
      checked: sourceGenreCheckboxOptionChecked,
      indeterminate: sourceGenreCheckboxOptionIndeterminate,
      children: sourceGenreCheckboxOptionChildrenTmp,
    };
  });
  return {
    ...genreCheckboxOptions,
    [source]: sourceGenreCheckboxOptionsTmp,
  };
};

/**
 * 删除单个勾选主品类时或删除全部勾选品类时 更新品类数据源类型与主、副品类Checkbox的Options集合的map映射 主品类的checked、indeterminate、children信息，副品类的disabled、checked信息
 * @param genreCheckboxOptions 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 * @param payload.source 品类数据源类型
 * @param payload.genre 当前删除的主品类value
 * @returns 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 */
const updateGenreCheckboxOptionsWhenGenreDelete: IGenreDeleteHandler = (genreCheckboxOptions, payload) => {
  /* ** 前置异常数据校验 ** */
  if (!genreCheckboxOptions) return {};
  const { source, genre } = payload || {};
  if (!source) return genreCheckboxOptions;
  const sourceGenreCheckboxOptions = genreCheckboxOptions[source];
  if (!Array.isArray(sourceGenreCheckboxOptions) || !sourceGenreCheckboxOptions.length) return genreCheckboxOptions;
  /* ** 前置异常数据校验 ** */

  // 有传具体的genre就是 删除单个已勾选的主品类，否则就是删除全部已勾选品类
  const isGenreDeleteItem = !!genre;
  const sourceGenreCheckboxOptionsTmp = sourceGenreCheckboxOptions.map((sourceGenreCheckboxOption) => {
    // 如果是删除单个主品类操作，并且当前主品类不是本次操作的主品类时，不做更新 原样返回
    if (isGenreDeleteItem && sourceGenreCheckboxOption?.value !== genre) return { ...sourceGenreCheckboxOption };
    const sourceSubGenreCheckboxOptions = sourceGenreCheckboxOption?.children || [];
    // 更新当前主品类下的副品类Checkbox的Options集合 全部重置为非禁用、非勾选状态
    // 使用递归方法处理所有层级的子品类（支持App Magic 4层级级联结构）
    const sourceSubGenreCheckboxOptionsTmp = updateChildrenCheckedRecursive(
      sourceSubGenreCheckboxOptions as IGenreCheckboxOption[],
      false,
    ).map((sourceSubGenreCheckboxOption) => {
      return {
        ...sourceSubGenreCheckboxOption,
        disabled: false,
      };
    });
    // 重置为relation为or、非勾选状态、非半选状态、副品类Checkbox的Options集合（重置状态后的）
    return {
      ...sourceGenreCheckboxOption,
      relation: Relation.Or,
      checked: false,
      indeterminate: false,
      children: sourceSubGenreCheckboxOptionsTmp || [],
    };
  });
  return {
    ...genreCheckboxOptions,
    [source]: sourceGenreCheckboxOptionsTmp,
  };
};

/**
 * 删除单个勾选主品类时 更新品类数据源类型与主、副品类Checkbox的Options集合的map映射 主品类的checked、indeterminate、children信息，副品类的disabled、checked信息
 * @param genreCheckboxOptions 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 * @param payload.source 品类数据源类型
 * @param payload.genre 当前删除的主品类value
 * @returns 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 */
export const updateGenreCheckboxOptionsWhenGenreDeleteItem: IGenreDeleteItemHandler = (
  genreCheckboxOptions,
  payload,
) => {
  return updateGenreCheckboxOptionsWhenGenreDelete(genreCheckboxOptions, payload);
};

/**
 * 删除所有已勾选品类时 更新品类数据源类型与主、副品类Checkbox的Options集合的map映射 主品类的checked、indeterminate、children信息，副品类的disabled、checked信息
 * @param genreCheckboxOptions 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 * @param payload.source 品类数据源类型
 * @returns 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 */
export const updateGenreCheckboxOptionsWhenGenreDeleteBatch: IGenreDeleteBatchHandler = (
  genreCheckboxOptions,
  payload,
) => {
  return updateGenreCheckboxOptionsWhenGenreDelete(genreCheckboxOptions, payload);
};

/**
 * ============================================
 * 多级嵌套递归辅助函数（App Magic 专用）
 * ============================================
 */

/**
 * 递归更新所有子级的选中状态
 * @param children 子级品类数组
 * @param checked 是否选中
 * @returns 更新后的子级品类数组
 */
export const updateChildrenCheckedRecursive = (
  children: IGenreCheckboxOption[],
  checked: boolean,
): IGenreCheckboxOption[] => {
  if (!Array.isArray(children) || children.length === 0) {
    return [];
  }

  return children.map((child) => {
    // @ts-ignore
    const subChildren = child?.children || [];
    return {
      ...child,
      checked,
      // 全选或全不选时，半选状态为 false
      indeterminate: false,
      children: subChildren.length ? updateChildrenCheckedRecursive(subChildren, checked) : subChildren,
    };
  });
};

/**
 * 递归计算父级的选中和半选状态
 * 规则：
 * - 所有子级都被完全勾选（checked=true 且 indeterminate=false）时，父级为全选状态
 * - 有任意子级被勾选或半选时，父级为半选状态（除非全选）
 * - 没有任何子级被勾选时，父级为未选中状态
 * @param children 子级品类数组
 * @returns { checked: 是否选中, indeterminate: 是否半选 }
 */
export const calculateIndeterminateRecursive = (
  children: IGenreCheckboxOption[],
): { checked: boolean; indeterminate: boolean } => {
  if (!Array.isArray(children) || children.length === 0) {
    return { checked: false, indeterminate: false };
  }

  let checkedCount = 0;
  let hasIndeterminate = false;

  for (const child of children) {
    const childChecked = !!child?.checked;
    // @ts-ignore
    const childIndeterminate = !!child?.indeterminate;
    // @ts-ignore
    if (childChecked && !childIndeterminate) {
      // 完全勾选的子级
      // eslint-disable-next-line no-plusplus
      checkedCount++;
    }
    // @ts-ignore
    if (childIndeterminate || (childChecked && childIndeterminate)) {
      // 半选状态的子级
      hasIndeterminate = true;
    }
    // 子级被勾选但有半选状态，也算部分选中
    if (childChecked) {
      hasIndeterminate = hasIndeterminate || !!childIndeterminate;
    }
  }

  const allChecked = checkedCount === children?.length;
  const someChecked = checkedCount > 0 || hasIndeterminate;

  if (allChecked && !hasIndeterminate) {
    // 所有子级都完全勾选，父级为全选
    return { checked: true, indeterminate: false };
  }

  if (someChecked) {
    // 部分子级被勾选或有半选状态，父级为半选
    return { checked: true, indeterminate: true };
  }

  // 没有任何子级被勾选
  return { checked: false, indeterminate: false };
};

/**
 * 递归查找并更新指定路径的品类节点，同时更新所有祖先节点的状态
 * @param genres 品类数组
 * @param path 品类路径（从主品类到目标品类的 value 数组）
 * @param updateFn 更新函数
 * @returns 更新后的品类数组
 */
export const updateGenreByPathRecursive = (
  genres: IGenreCheckboxOption[],
  path: string[],
  updateFn: (node: IGenreCheckboxOption) => IGenreCheckboxOption,
): IGenreCheckboxOption[] => {
  if (!Array.isArray(genres) || genres.length === 0) {
    return [];
  }

  if (!Array.isArray(path) || path.length === 0) {
    return genres;
  }

  const [currentValue, ...restPath] = path;

  return genres.map((genre) => {
    if (genre?.value !== currentValue) {
      return genre;
    }

    // 找到目标节点
    if (restPath.length === 0) {
      return updateFn(genre);
    }

    // 继续递归查找子级
    // @ts-ignore
    const updatedChildren = updateGenreByPathRecursive((genre?.children as any[]) || [], restPath, updateFn);
    const { checked, indeterminate } = calculateIndeterminateRecursive(updatedChildren);

    return {
      ...genre,
      children: updatedChildren,
      checked,
      indeterminate,
    };
  });
};

/**
 * App Magic 级联品类全选/取消全选处理
 * @param genreCheckboxOptions 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 * @param payload.source 品类数据源类型
 * @param payload.path 从根节点到父节点的路径（value 数组），空数组表示全选所有
 * @param payload.checked 是否全选
 * @returns 更新后的品类数据源类型与主、副品类Checkbox的Options集合的map映射
 */
export const updateGenreCheckboxOptionsWhenCascadeAllCheckedChange = (
  genreCheckboxOptions: IGenreSourcesGenreCheckboxOptionsWithChildren,
  payload: { source: string; path: string[]; checked: boolean },
): IGenreSourcesGenreCheckboxOptionsWithChildren => {
  if (!genreCheckboxOptions) {
    return {};
  }

  const { source, path, checked } = payload || {};
  if (!source) {
    return genreCheckboxOptions;
  }

  const sourceGenreCheckboxOptions = genreCheckboxOptions[source];
  if (!Array.isArray(sourceGenreCheckboxOptions) || sourceGenreCheckboxOptions.length === 0) {
    return genreCheckboxOptions;
  }

  // 如果 path 为空，全选/取消全选所有根级品类
  if (!Array.isArray(path) || path.length === 0) {
    const updatedOptions = sourceGenreCheckboxOptions.map((genre) => {
      const subChildren = genre?.children || [];
      return {
        ...genre,
        checked,
        indeterminate: false,
        children: subChildren.length
          ? updateChildrenCheckedRecursive(subChildren as IGenreCheckboxOption[], checked)
          : subChildren,
      };
    });

    return {
      ...genreCheckboxOptions,
      [source]: updatedOptions,
    };
  }

  // 找到指定路径的父节点，全选/取消全选其所有子节点
  const updateFn = (node: IGenreCheckboxOption): IGenreCheckboxOption => {
    // @ts-ignore
    const subNodes = node?.children || [];
    if (!Array.isArray(subNodes) || subNodes.length === 0) {
      return node;
    }

    const updatedChildren = subNodes.map((child) => {
      const subChildren = child?.children || [];
      return {
        ...child,
        checked,
        indeterminate: false,
        children: subChildren.length
          ? updateChildrenCheckedRecursive(subChildren as IGenreCheckboxOption[], checked)
          : subChildren,
      };
    });

    const { checked: parentChecked, indeterminate } = calculateIndeterminateRecursive(
      updatedChildren as IGenreCheckboxOption[],
    );

    return {
      ...node,
      checked: parentChecked,
      // @ts-ignore
      indeterminate,
      children: updatedChildren,
    };
  };

  const updatedOptions = updateGenreByPathRecursive(
    sourceGenreCheckboxOptions as IGenreCheckboxOption[],
    path,
    updateFn,
  );

  return {
    ...genreCheckboxOptions,
    [source]: updatedOptions,
  } as any;
};

/**
 * App Magic 级联品类搜索状态下的批量勾选/取消勾选处理
 * 搜索状态下 Select All 时，只勾选搜索过滤后可见的品类（及其子品类链路）
 * 搜索状态下 Clear All 时，取消勾选该层级所有品类（包括递归取消勾选子品类）
 * @param genreCheckboxOptions 全量品类数据
 * @param payload { source, path, checked, filteredValues: 搜索过滤后可见的品类 value 集合 }
 */
export const updateGenreCheckboxOptionsWhenCascadeBatchCheckedChange = (
  genreCheckboxOptions: IGenreSourcesGenreCheckboxOptionsWithChildren,
  payload: {
    source: string;
    path: string[];
    checked: boolean;
    filteredValues: string[];
    filteredGenres: IGenreCheckboxOptionWithChildren[];
  },
): IGenreSourcesGenreCheckboxOptionsWithChildren => {
  if (!genreCheckboxOptions) {
    return {};
  }

  const { source, path, checked, filteredValues, filteredGenres } = payload || {};
  if (!source || !Array.isArray(filteredValues)) {
    return genreCheckboxOptions;
  }

  const sourceGenreCheckboxOptions = genreCheckboxOptions[source];
  if (!Array.isArray(sourceGenreCheckboxOptions) || sourceGenreCheckboxOptions.length === 0) {
    return genreCheckboxOptions;
  }

  // 构建搜索过滤后可见品类 value 的 Set，便于快速查找
  const filteredValueSet = new Set(filteredValues);

  /**
   * 从搜索过滤后的品类树中查找某个品类的 children
   * @param value 品类 value
   * @param searchTree 搜索过滤后的品类树
   * @returns 该品类在过滤树中的 children，如果找不到则返回 undefined
   */
  const findFilteredChildren = (
    value: string,
    searchTree: IGenreCheckboxOptionWithChildren[],
  ): IGenreCheckboxOptionWithChildren[] | undefined => {
    for (const node of searchTree) {
      if (node?.value === value) {
        return node?.children as IGenreCheckboxOptionWithChildren[] | undefined;
      }
      const children = node?.children as IGenreCheckboxOptionWithChildren[] | undefined;
      if (Array.isArray(children) && children.length > 0) {
        const found = findFilteredChildren(value, children);
        if (found !== undefined) {
          return found;
        }
      }
    }
    return undefined;
  };

  /**
   * 递归勾选品类，只勾选在搜索过滤结果中可见的品类
   * @param fullChildren 全量数据中的子品类数组
   * @param filteredTree 搜索过滤后对应层级的品类数组
   */
  const updateChildrenByFilter = (
    fullChildren: IGenreCheckboxOption[],
    filteredTree: IGenreCheckboxOptionWithChildren[],
  ): IGenreCheckboxOption[] => {
    if (!Array.isArray(fullChildren) || fullChildren.length === 0) {
      return [];
    }

    // 构建过滤树中的 value -> node 映射
    const filteredMap = new Map<string, IGenreCheckboxOptionWithChildren>();
    if (Array.isArray(filteredTree)) {
      filteredTree.forEach((node) => {
        if (node?.value) {
          filteredMap.set(node.value, node);
        }
      });
    }

    return fullChildren.map((child) => {
      const filteredNode = filteredMap.get(child?.value);
      // @ts-ignore
      const subChildren = child?.children || [];

      if (filteredNode) {
        // 品类在搜索结果中可见
        const filteredNodeChildren = filteredNode?.children as IGenreCheckboxOptionWithChildren[] | undefined;
        const hasFilteredChildren = Array.isArray(filteredNodeChildren) && filteredNodeChildren.length > 0;
        const hasFullChildren = Array.isArray(subChildren) && subChildren.length > 0;

        if (hasFullChildren && hasFilteredChildren) {
          // 有子品类且过滤树中也有对应的子品类，递归处理
          const updatedSubChildren = updateChildrenByFilter(
            subChildren as IGenreCheckboxOption[],
            filteredNodeChildren,
          );
          const { checked: parentChecked, indeterminate } = calculateIndeterminateRecursive(updatedSubChildren);
          return {
            ...child,
            checked: parentChecked,
            indeterminate,
            children: updatedSubChildren,
          };
        }
        if (hasFullChildren && !hasFilteredChildren) {
          // 当前节点自身匹配搜索，保留所有原始子级并全部勾选
          return {
            ...child,
            checked: true,
            indeterminate: false,
            children: updateChildrenCheckedRecursive(subChildren as IGenreCheckboxOption[], true),
          };
        }
        // 叶子节点，直接勾选
        return {
          ...child,
          checked: true,
          indeterminate: false,
        };
      }

      // 品类不在搜索结果中，保持原样
      return child;
    });
  };

  // 如果 path 为空，批量操作所有根级品类
  if (!Array.isArray(path) || path.length === 0) {
    const updatedOptions = sourceGenreCheckboxOptions.map((genre) => {
      if (!filteredValueSet.has(genre?.value)) {
        // 不在过滤结果中，保持原样
        return genre;
      }

      const subChildren = genre?.children || [];
      // 查找该品类在过滤树中的 children
      const filteredNode = filteredGenres.find((n) => n?.value === genre?.value);
      const filteredNodeChildren = filteredNode?.children as IGenreCheckboxOptionWithChildren[] | undefined;
      const hasFilteredChildren = Array.isArray(filteredNodeChildren) && filteredNodeChildren.length > 0;
      const hasFullChildren = Array.isArray(subChildren) && subChildren.length > 0;

      if (hasFullChildren && hasFilteredChildren) {
        const updatedSubChildren = updateChildrenByFilter(subChildren as IGenreCheckboxOption[], filteredNodeChildren);
        const { checked: parentChecked, indeterminate } = calculateIndeterminateRecursive(updatedSubChildren);
        return {
          ...genre,
          checked: parentChecked,
          indeterminate,
          children: updatedSubChildren,
        };
      }
      if (hasFullChildren && !hasFilteredChildren) {
        // 当前节点自身匹配搜索，保留所有原始子级并全部勾选
        return {
          ...genre,
          checked,
          indeterminate: false,
          children: updateChildrenCheckedRecursive(subChildren as IGenreCheckboxOption[], checked),
        };
      }
      // 叶子节点
      return {
        ...genre,
        checked,
        indeterminate: false,
      };
    });

    return {
      ...genreCheckboxOptions,
      [source]: updatedOptions,
    };
  }

  // 有 path：找到指定路径的父节点，批量操作其子节点中在过滤结果中可见的品类
  const updateFn = (node: IGenreCheckboxOption): IGenreCheckboxOption => {
    // @ts-ignore
    const subNodes = node?.children || [];
    if (!Array.isArray(subNodes) || subNodes.length === 0) {
      return node;
    }

    // 获取该 path 节点在过滤树中对应的 children
    let filteredChildrenTree: IGenreCheckboxOptionWithChildren[] = [];
    // 从过滤树中根据 path 导航到对应节点
    let currentFilteredLevel: IGenreCheckboxOptionWithChildren[] = filteredGenres;
    for (const pathValue of path) {
      const matchedNode = currentFilteredLevel?.find((n) => n?.value === pathValue);
      if (matchedNode) {
        currentFilteredLevel = (matchedNode?.children as IGenreCheckboxOptionWithChildren[]) || [];
      } else {
        currentFilteredLevel = [];
        break;
      }
    }
    filteredChildrenTree = currentFilteredLevel;

    const updatedChildren = updateChildrenByFilter(subNodes as IGenreCheckboxOption[], filteredChildrenTree);
    const { checked: parentChecked, indeterminate } = calculateIndeterminateRecursive(
      updatedChildren as IGenreCheckboxOption[],
    );

    return {
      ...node,
      checked: parentChecked,
      // @ts-ignore
      indeterminate,
      children: updatedChildren,
    };
  };

  const updatedOptions = updateGenreByPathRecursive(
    sourceGenreCheckboxOptions as IGenreCheckboxOption[],
    path,
    updateFn,
  );

  return {
    ...genreCheckboxOptions,
    [source]: updatedOptions,
  } as any;
};

/**
 * 递归收集所有选中的品类值
 * @param genres 品类数组
 * @returns 选中的品类值数组
 */
export const collectCheckedValuesRecursive = (genres: IGenreCheckboxOption[]): string[] => {
  if (!Array.isArray(genres) || genres.length === 0) {
    return [];
  }

  const result: string[] = [];

  genres.forEach((genre) => {
    const genreValue = genre?.value;
    if (genreValue && genre?.checked) {
      result.push(genreValue);
    }

    // @ts-ignore
    const subGenres = genre?.children || [];

    if (subGenres.length) {
      const childValues = collectCheckedValuesRecursive(subGenres as IGenreCheckboxOption[]);
      result.push(...childValues);
    }
  });

  return result;
};

/**
 * 根据路径取消勾选指定品类（支持多层级）
 * 点击tag时，根据tag中存储的完整路径，精准取消勾选对应的品类
 * @param genreCheckboxOptions 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 * @param payload 参数对象
 * @param payload.source 品类数据源类型
 * @param payload.path 从根节点到目标节点的路径（value 数组）
 * @returns 更新后的品类数据源类型与主、副品类Checkbox的Options集合的map映射
 */
export const updateGenreCheckboxOptionsWhenCascadeDeleteItem = (
  genreCheckboxOptions: IGenreSourcesGenreCheckboxOptionsWithChildren,
  payload: { source: string; path: string[] },
): IGenreSourcesGenreCheckboxOptionsWithChildren => {
  if (!genreCheckboxOptions) {
    return {};
  }

  const { source, path } = payload || {};
  if (!source || !Array.isArray(path) || path.length === 0) {
    return genreCheckboxOptions;
  }

  const sourceGenreCheckboxOptions = genreCheckboxOptions[source];
  if (!Array.isArray(sourceGenreCheckboxOptions) || sourceGenreCheckboxOptions.length === 0) {
    return genreCheckboxOptions;
  }

  // 取消勾选目标节点及其所有子节点
  const updateFn = (node: IGenreCheckboxOption): IGenreCheckboxOption => {
    // @ts-ignore
    const subNodes = node?.children || [];
    const updatedChildren =
      subNodes.length > 0 ? updateChildrenCheckedRecursive(subNodes as IGenreCheckboxOption[], false) : subNodes;

    return {
      ...node,
      checked: false,
      indeterminate: false,
      children: updatedChildren,
    };
  };

  const updatedOptions = updateGenreByPathRecursive(
    sourceGenreCheckboxOptions as IGenreCheckboxOption[],
    path,
    updateFn,
  );

  return {
    ...genreCheckboxOptions,
    [source]: updatedOptions,
  } as any;
};

/**
 * ============================================
 * App Magic 级联快捷勾选处理（专用方法）
 * ============================================
 */

/**
 * 递归清空所有品类的勾选状态（用于 Replace 模式）
 * @param genres 品类数组
 * @returns 清空勾选状态后的品类数组
 */
const clearAllCheckedRecursive = (genres: IGenreCheckboxOption[]): IGenreCheckboxOption[] => {
  if (!Array.isArray(genres) || genres.length === 0) {
    return [];
  }

  return genres.map((genre) => {
    // @ts-ignore
    const subGenres = genre?.children || [];
    return {
      ...genre,
      checked: false,
      indeterminate: false,
      children: subGenres.length ? clearAllCheckedRecursive(subGenres) : subGenres,
    };
  });
};

/**
 * 递归查找所有匹配指定 value 的节点路径
 * @param genres 品类数组
 * @param targetValue 目标品类 value
 * @param currentPath 当前路径
 * @param matchType 匹配模式：全等（默认）或包含
 * @returns 所有匹配节点的路径数组
 */
const findMatchingPathsRecursive = (
  genres: IGenreCheckboxOption[],
  targetValue: string,
  currentPath: string[] = [],
  matchType: ShortcutMatchType = ShortcutMatchType.Exact,
): string[][] => {
  if (!Array.isArray(genres) || genres.length === 0) {
    return [];
  }

  const matchingPaths: string[][] = [];

  for (const genre of genres) {
    const genreValue = genre?.value;
    if (!genreValue) continue;

    const newPath = [...currentPath, genreValue];

    // 判断当前节点是否匹配
    const isMatch =
      matchType === ShortcutMatchType.Exact ? genreValue === targetValue : genreValue.includes(targetValue);

    // 如果当前节点匹配，记录路径
    if (isMatch) {
      matchingPaths.push(newPath);
    }

    // @ts-ignore
    const subGenres = genre?.children || [];
    if (subGenres.length > 0) {
      // 递归查找子级
      const childMatches = findMatchingPathsRecursive(
        subGenres as IGenreCheckboxOption[],
        targetValue,
        newPath,
        matchType,
      );
      matchingPaths.push(...childMatches);
    }
  }

  return matchingPaths;
};

/**
 * App Magic 级联品类快捷勾选处理（专用方法，不复用两层级逻辑）
 *
 * 从第1层开始深度优先搜索所有匹配 shortcutValue 的节点，并勾选该节点及其所有子级品类
 * 支持多次点击切换：如果已勾选则取消勾选，如果未勾选则执行勾选
 * 支持两种模式：
 * - Compose: 在现有勾选基础上增加/删除匹配品类
 * - Replace: 清空所有勾选，只勾选匹配的品类
 *
 * @param genreCheckboxOptions 品类 Checkbox Options 集合
 * @param payload.source 数据源类型
 * @param payload.shortcut 快捷勾选按钮配置
 * @returns 更新后的品类 Checkbox Options 集合
 */
export const updateGenreCheckboxOptionsWhenCascadeShortcuts = (
  genreCheckboxOptions: IGenreSourcesGenreCheckboxOptionsWithChildren,
  payload: { source: string; shortcut: IShortcut },
): IGenreSourcesGenreCheckboxOptionsWithChildren => {
  if (!genreCheckboxOptions) {
    return {};
  }

  const { source, shortcut } = payload || {};
  const {
    value: shortcutValue,
    disabled = false,
    matchType = ShortcutMatchType.Exact,
    type = ShortcutActionType.Compose,
  } = shortcut || {};

  if (!source || !shortcutValue) {
    return genreCheckboxOptions;
  }

  const sourceGenreCheckboxOptions = genreCheckboxOptions[source];
  if (!Array.isArray(sourceGenreCheckboxOptions) || sourceGenreCheckboxOptions.length === 0) {
    return genreCheckboxOptions;
  }

  // 查找所有匹配的路径
  const matchingPaths = findMatchingPathsRecursive(
    sourceGenreCheckboxOptions as IGenreCheckboxOption[],
    shortcutValue,
    [],
    matchType,
  );

  if (matchingPaths.length === 0) {
    // 没有找到匹配的品类，原样返回
    return genreCheckboxOptions;
  }

  // 判断是 Replace 还是 Compose 模式
  const isReplaceMode = type === ShortcutActionType.Replace;
  // 切换模式：检查是否有已勾选的匹配节点
  // 如果快捷按钮显示为 disabled 状态，说明当前是勾选状态，点击后应该取消勾选
  // 否则执行勾选操作
  const shouldCheck = !disabled;

  // 对每个匹配的路径执行级联勾选/取消勾选
  let updatedOptions = sourceGenreCheckboxOptions as IGenreCheckboxOption[];

  if (isReplaceMode && shouldCheck) {
    // Replace 模式 + 勾选操作：
    // 1. 先清空所有勾选
    updatedOptions = clearAllCheckedRecursive(updatedOptions);
    // 2. 再勾选所有匹配的品类及其子节点
    for (const path of matchingPaths) {
      updatedOptions = updateGenreByPathRecursive(updatedOptions, path, (node) => {
        // @ts-ignore
        const subNodes = node?.children || [];
        const updatedChildren =
          subNodes.length > 0 ? updateChildrenCheckedRecursive(subNodes as IGenreCheckboxOption[], true) : subNodes;

        return {
          ...node,
          checked: true,
          indeterminate: false,
          children: updatedChildren,
        };
      });
    }
  } else {
    // Compose 模式或取消勾选操作：保持现有逻辑
    for (const path of matchingPaths) {
      if (shouldCheck) {
        // 勾选模式：递归勾选该路径及所有子节点
        updatedOptions = updateGenreByPathRecursive(updatedOptions, path, (node) => {
          // @ts-ignore
          const subNodes = node?.children || [];
          const updatedChildren =
            subNodes.length > 0 ? updateChildrenCheckedRecursive(subNodes as IGenreCheckboxOption[], true) : subNodes;

          return {
            ...node,
            checked: true,
            indeterminate: false,
            children: updatedChildren,
          };
        });
      } else {
        // 取消勾选模式：递归取消勾选该路径及所有子节点
        updatedOptions = updateGenreByPathRecursive(updatedOptions, path, (node) => {
          // @ts-ignore
          const subNodes = node?.children || [];
          const updatedChildren =
            subNodes.length > 0 ? updateChildrenCheckedRecursive(subNodes as IGenreCheckboxOption[], false) : subNodes;

          return {
            ...node,
            checked: false,
            indeterminate: false,
            children: updatedChildren,
          };
        });
      }
    }
  }

  // 重新计算所有祖先节点的 checked/indeterminate 状态
  // 通过自底向上的方式更新所有受影响路径的状态
  const updatedOptionsWithAncestorState = recalculateAncestorStatesRecursive(updatedOptions);

  return {
    ...genreCheckboxOptions,
    [source]: updatedOptionsWithAncestorState,
  } as any;
};

/**
 * 递归重新计算所有祖先节点的 checked/indeterminate 状态
 * @param genres 品类数组
 * @returns 更新状态后的品类数组
 */
const recalculateAncestorStatesRecursive = (genres: IGenreCheckboxOption[]): IGenreCheckboxOption[] => {
  if (!Array.isArray(genres) || genres.length === 0) {
    return [];
  }

  return genres.map((genre) => {
    // @ts-ignore
    const subGenres = genre?.children || [];

    if (subGenres.length > 0) {
      // 先递归处理子级
      const updatedChildren = recalculateAncestorStatesRecursive(subGenres as IGenreCheckboxOption[]);

      // 根据子级状态重新计算当前节点的状态
      const { checked, indeterminate } = calculateIndeterminateRecursive(updatedChildren as IGenreCheckboxOption[]);

      return {
        ...genre,
        checked,
        indeterminate,
        children: updatedChildren,
      };
    }

    // 叶子节点，状态保持不变
    return genre;
  });
};

/**
 * 构建过滤后品类的值集合（用于搜索模式下的单选操作）
 * @param filteredGenres 过滤后的品类树
 * @param path 从根节点到目标节点的路径（value 数组）
 * @returns 在过滤结果中，该路径下所有叶子节点的值集合
 */
const collectFilteredLeafValues = (
  filteredGenres: IGenreCheckboxOptionWithChildren[],
  path: string[],
): Set<string> => {
  const result = new Set<string>();

  if (!Array.isArray(filteredGenres) || filteredGenres.length === 0) {
    return result;
  }

  if (!Array.isArray(path) || path.length === 0) {
    return result;
  }

  // 根据路径找到目标节点在过滤后的树中的位置
  const findNodeByPath = (
    nodes: IGenreCheckboxOptionWithChildren[],
    remainingPath: string[],
  ): IGenreCheckboxOptionWithChildren | null => {
    if (!Array.isArray(nodes) || nodes.length === 0) {
      return null;
    }

    const [currentValue, ...restPath] = remainingPath;
    const targetNode = nodes.find((node) => node?.value === currentValue);

    if (!targetNode) {
      return null;
    }

    if (restPath.length === 0) {
      return targetNode;
    }

    const children = targetNode.children as IGenreCheckboxOptionWithChildren[] | undefined;
    if (!Array.isArray(children) || children.length === 0) {
      return null;
    }

    return findNodeByPath(children, restPath);
  };

  // 递归收集所有叶子节点的值
  const collectLeaves = (node: IGenreCheckboxOptionWithChildren): void => {
    const children = node?.children as IGenreCheckboxOptionWithChildren[] | undefined;

    if (!Array.isArray(children) || children.length === 0) {
      // 叶子节点
      if (node?.value) {
        result.add(node.value);
      }
      return;
    }

    // 非叶子节点，递归收集子节点
    children.forEach((child) => collectLeaves(child));
  };

  const targetNode = findNodeByPath(filteredGenres, path);
  if (targetNode) {
    collectLeaves(targetNode);
  }

  return result;
};

/**
 * 递归更新子级的选中状态（仅更新在过滤结果中的节点）
 * @param children 子级品类数组
 * @param checked 是否选中
 * @param filteredLeafValues 过滤结果中的叶子节点值集合
 * @returns 更新后的子级品类数组
 */
const updateChildrenCheckedByFilter = (
  children: IGenreCheckboxOption[],
  checked: boolean,
  filteredLeafValues: Set<string>,
): IGenreCheckboxOption[] => {
  if (!Array.isArray(children) || children.length === 0) {
    return [];
  }

  return children.map((child) => {
    // @ts-ignore
    const subChildren = child?.children || [];
    const hasChildren = Array.isArray(subChildren) && subChildren.length > 0;

    if (hasChildren) {
      // 非叶子节点，递归处理子节点
      const updatedChildren = updateChildrenCheckedByFilter(
        subChildren as IGenreCheckboxOption[],
        checked,
        filteredLeafValues,
      );
      // 根据子节点状态计算当前节点状态
      const { checked: parentChecked, indeterminate } = calculateIndeterminateRecursive(updatedChildren);
      return {
        ...child,
        checked: parentChecked,
        indeterminate,
        children: updatedChildren,
      };
    }

    // 叶子节点：只有在过滤结果中的才更新
    if (filteredLeafValues.has(child?.value)) {
      return {
        ...child,
        checked,
        indeterminate: false,
      };
    }

    // 不在过滤结果中，保持原状态
    return { ...child };
  });
};

/**
 * App Magic 级联品类勾选处理
 * 根据 filteredGenres 参数仅勾选/取消勾选可见的路径：
 * - 搜索模式下：filteredGenres 为过滤后的品类树，仅勾选匹配搜索结果的路径
 * - 非搜索模式下：filteredGenres 为全量品类树，勾选全部子路径
 * @param genreCheckboxOptions 品类数据源类型与主、副品类Checkbox的Options集合的map映射
 * @param payload.source 品类数据源类型
 * @param payload.path 从根节点到目标节点的路径（value 数组）
 * @param payload.checked 是否勾选
 * @param payload.filteredGenres 当前可见的品类树（搜索模式为过滤结果，非搜索模式为全量数据）
 * @returns 更新后的品类数据源类型与主、副品类Checkbox的Options集合的map映射
 */
export const updateGenreCheckboxOptionsWhenCascadeCheckedChangeByFilter = (
  genreCheckboxOptions: IGenreSourcesGenreCheckboxOptionsWithChildren,
  payload: {
    source: string;
    path: string[];
    checked: boolean;
    filteredGenres: IGenreCheckboxOptionWithChildren[];
  },
): IGenreSourcesGenreCheckboxOptionsWithChildren => {
  if (!genreCheckboxOptions) {
    return {};
  }

  const { source, path, checked, filteredGenres } = payload || {};
  if (!source || !Array.isArray(path) || path.length === 0) {
    return genreCheckboxOptions;
  }

  const sourceGenreCheckboxOptions = genreCheckboxOptions[source];
  if (!Array.isArray(sourceGenreCheckboxOptions) || sourceGenreCheckboxOptions.length === 0) {
    return genreCheckboxOptions;
  }

  // 收集过滤结果中该路径下的所有叶子节点
  const filteredLeafValues = collectFilteredLeafValues(filteredGenres, path);

  // 如果没有匹配的叶子节点，直接返回
  if (filteredLeafValues.size === 0) {
    return genreCheckboxOptions;
  }

  // 更新目标节点及其所有子节点（仅更新在过滤结果中的）
  const updateFn = (node: IGenreCheckboxOption): IGenreCheckboxOption => {
    // @ts-ignore
    const subNodes = node?.children || [];

    if (!Array.isArray(subNodes) || subNodes.length === 0) {
      // 叶子节点：只有在过滤结果中的才更新
      if (filteredLeafValues.has(node?.value)) {
        return {
          ...node,
          checked,
          indeterminate: false,
        };
      }
      return { ...node };
    }

    // 非叶子节点，递归处理子节点
    const updatedChildren = updateChildrenCheckedByFilter(
      subNodes as IGenreCheckboxOption[],
      checked,
      filteredLeafValues,
    );

    // 根据子节点状态计算当前节点状态
    const { checked: parentChecked, indeterminate } = calculateIndeterminateRecursive(updatedChildren);

    return {
      ...node,
      checked: parentChecked,
      // @ts-ignore
      indeterminate,
      children: updatedChildren,
    };
  };

  const updatedOptions = updateGenreByPathRecursive(
    sourceGenreCheckboxOptions as IGenreCheckboxOption[],
    path,
    updateFn,
  );

  return {
    ...genreCheckboxOptions,
    [source]: updatedOptions,
  } as any;
};
